import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import BrandLogo from "../components/BrandLogo";
import { HandHeart } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const [form, setForm] = useState({
        name: "", email: "", password: "", role: "individual", organization: "",
    });
    const [loading, setLoading] = useState(false);
    const [eulaOk, setEulaOk] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    // Derive referral code during render (URL takes priority over previously stored)
    const urlRef = (params.get("ref") || "").trim();
    const storedRef = typeof window !== "undefined" ? (localStorage.getItem("ll_ref") || "").trim() : "";
    const refCode = urlRef || storedRef;

    // Persist + track click only when URL had a fresh code (not on every render)
    useEffect(() => {
        if (!urlRef) return;
        localStorage.setItem("ll_ref", urlRef);
        api.post("/referrals/track", { code: urlRef }).catch(() => {});
    }, [urlRef]);

    const submit = async (e) => {
        e.preventDefault();
        if (!eulaOk) {
            toast.error("Please acknowledge the End-User Agreement to continue.");
            return;
        }
        setLoading(true);
        const payload = {
            ...form,
            eula_accepted: true,
            ...(refCode ? { ref: refCode } : {}),
        };
        const res = await register(payload);
        setLoading(false);
        if (res.ok) {
            if (refCode) localStorage.removeItem("ll_ref");
            toast.success("Account created");
            nav("/dashboard");
        }
        else { toast.error(res.error || "Could not register"); }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white">
            <div className="hidden md:block relative">
                <img src="https://images.pexels.com/photos/18800734/pexels-photo-18800734.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-[#0F1E3D]/50" />
                <div className="relative h-full p-12 flex flex-col justify-between text-white">
                    <Link to="/" className="flex items-center gap-2">
                        <BrandLogo height={40} className="brightness-0 invert" />
                    </Link>
                    <div>
                        <p className="overline text-white/70">Get started</p>
                        <h2 className="font-serif text-4xl mt-3 leading-tight max-w-md">
                            One workbench<br/><span className="italic">for both sides</span> of the table.
                        </h2>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-center p-8">
                <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="register-form">
                    <div>
                        <p className="overline">Create an account</p>
                        <h1 className="font-serif text-4xl mt-3 leading-tight">Tell us who you are.</h1>
                    </div>

                    {refCode && (
                        <div className="flex items-start gap-2.5 rounded-md border border-emerald-200 bg-emerald-50 px-3.5 py-2.5" data-testid="register-referral-banner">
                            <HandHeart size={18} weight="duotone" className="text-emerald-700 shrink-0 mt-0.5" />
                            <div className="text-xs text-emerald-900 leading-snug">
                                You were referred by code <span className="font-mono font-semibold">{refCode}</span>. They&apos;ll earn a commission if you upgrade.
                            </div>
                        </div>
                    )}

                    <div>
                        <Label>I am a…</Label>
                        <RadioGroup value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}
                            className="grid grid-cols-2 gap-3 mt-2" data-testid="register-role">
                            <label className={`border rounded-md p-4 cursor-pointer transition-all ${form.role === "individual" ? "border-sky-500 bg-sky-200 ring-2 ring-sky-500" : "border-sky-300 bg-sky-100 hover:bg-sky-200"}`}>
                                <RadioGroupItem value="individual" className="sr-only" data-testid="role-individual" />
                                <p className="text-sm font-semibold">Individual</p>
                                <p className="text-xs text-slate-600 mt-1">Disputing a lien/judgment against me</p>
                            </label>
                            <label className={`border rounded-md p-4 cursor-pointer transition-all ${form.role === "business" ? "border-fuchsia-500 bg-fuchsia-200 ring-2 ring-fuchsia-500" : "border-fuchsia-300 bg-fuchsia-100 hover:bg-fuchsia-200"}`}>
                                <RadioGroupItem value="business" className="sr-only" data-testid="role-business" />
                                <p className="text-sm font-semibold">Business / Pro</p>
                                <p className="text-xs text-slate-600 mt-1">Attorney, agency or creditor</p>
                            </label>
                        </RadioGroup>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="name">Full name</Label>
                            <Input data-testid="register-name" id="name" required value={form.name} onChange={set("name")} className="mt-1.5 h-11" />
                        </div>
                        <div>
                            <Label htmlFor="org">Organization {form.role === "individual" && <span className="text-slate-400">(optional)</span>}</Label>
                            <Input data-testid="register-organization" id="org" value={form.organization} onChange={set("organization")} className="mt-1.5 h-11" />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="email">Email</Label>
                        <Input data-testid="register-email" id="email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 h-11" />
                    </div>
                    <div>
                        <Label htmlFor="pw">Password</Label>
                        <Input data-testid="register-password" id="pw" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1.5 h-11" />
                    </div>

                    <label className="flex items-start gap-2.5 text-xs text-slate-700 leading-snug cursor-pointer select-none" data-testid="register-eula-label">
                        <input
                            type="checkbox"
                            checked={eulaOk}
                            onChange={(e) => setEulaOk(e.target.checked)}
                            required
                            data-testid="register-eula-checkbox"
                            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#0F1E3D] focus:ring-[#0F1E3D] shrink-0"
                        />
                        <span>
                            I acknowledge that <strong>LienLift Direct is NOT a law firm</strong> and does not provide legal advice —
                            it is a document-preparation and information service only. I have read and agree to the{" "}
                            <Link
                                to="/eula"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#2563EB] hover:underline font-medium"
                                data-testid="register-eula-link"
                            >
                                End-User Agreement
                            </Link>.
                        </span>
                    </label>

                    <Button data-testid="register-submit" type="submit" disabled={loading || !eulaOk} className="w-full h-11 bg-[#0F1E3D] hover:bg-[#1A2C57] rounded-md disabled:opacity-50">
                        {loading ? "Creating…" : "Create account"}
                    </Button>
                    <p className="text-sm text-slate-600 text-center">
                        Already have an account? <Link data-testid="link-login" to="/login" className="text-[#2563EB] hover:underline">Sign in</Link>
                    </p>
                </form>
            </div>
        </div>
    );
}
